import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import InfoIcon from "@material-ui/icons/Info";
import { startOfDay } from "date-fns";
import { PeriodType } from "../../../common/data/service-proxies";
import { useFuelPercentageForIdlingTime } from "../hooks/useIdleReportsHooks";
import { IdleReportsHookParams } from "../hooks/useIdleReportsHooks";
import { useProLogsTheme } from "../../../common/hooks/useProLogsTheme";
import ReactECharts from "echarts-for-react";
import "../fuel-economy-report/FuelEconomyReport.css";

const buildDayLabels = (
  from: Date,
  to: Date,
  periodType: PeriodType,
): string[] => {
  const result: string[] = [];
  const fromStart = startOfDay(from);
  const toStart = startOfDay(to);
  const oneDay = 24 * 60 * 60 * 1000;

  if (periodType === PeriodType.Weekly) {
    for (let d = fromStart.getTime(); d <= toStart.getTime(); d += oneDay) {
      result.push(
        new Date(d).toLocaleDateString("en-US", { weekday: "short" }),
      );
    }
  } else {
    const days: Date[] = [];
    for (let d = fromStart.getTime(); d <= toStart.getTime(); d += oneDay) {
      days.push(new Date(d));
    }
    const byWeek: { [bucket: string]: number } = {};
    days.forEach((d) => {
      const dayOfMonth = d.getDate();
      const bucket = `${Math.floor((dayOfMonth - 1) / 7) + 1}`;
      byWeek[bucket] = 1;
    });
    Object.keys(byWeek)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((b) => result.push(`W${b}`));
  }

  return result;
};

export const TotalFuelSpentIdleWidget: React.FC<IdleReportsHookParams> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const theme = useProLogsTheme();
  const { data, loading: isLoading, error } = useFuelPercentageForIdlingTime({
    fromDate,
    toDate,
    periodType,
  });
  const isError = !!error;

  const chartOption = useMemo(() => {
    if (!data?.values?.length) return null;
    const labels = buildDayLabels(
      fromDate,
      toDate,
      periodType ?? PeriodType.Weekly,
    );
    const rawValues = data.values;
    const len = Math.min(labels.length, rawValues.length);
    if (len === 0) return null;
    const values = rawValues.slice(0, len);
    const displayLabels = labels.slice(0, len);
    const maxVal = Math.max(...values, 25);
    const xMax = Math.ceil(maxVal / 5) * 5 || 25;

    return {
      tooltip: {
        trigger: "axis",
        formatter: (params: unknown) => {
          const p = Array.isArray(params) ? params[0] : null;
          if (p && "name" in p && "value" in p) {
            return `${(p as { name: string; value: number }).name}: ${Number(
              (p as { value: number }).value,
            ).toFixed(2)}%`;
          }
          return "";
        },
      },
      grid: {
        left: "8%",
        right: "8%",
        bottom: "10%",
        top: "10%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        min: 0,
        max: xMax,
        axisLabel: {
          color: theme.palette.text.primary,
        },
        splitLine: {
          lineStyle: {
            color: theme.palette.divider,
          },
        },
      },
      yAxis: {
        type: "category",
        data: displayLabels,
        axisLabel: {
          color: theme.palette.text.primary,
        },
      },
      series: [
        {
          type: "bar",
          data: values,
          barWidth: "60%",
          itemStyle: {
            color: "#3b82f6",
          },
          barBorderRadius: [0, 4, 4, 0],
        },
      ],
    };
  }, [
    data,
    fromDate,
    toDate,
    periodType,
    theme.palette.text.primary,
    theme.palette.divider,
  ]);

  const hasData = useMemo(() => (data?.values?.length ?? 0) > 0, [data]);

  return (
    <Card
      className="widget"
      style={{ minHeight: 0, display: "flex", flexDirection: "column" }}
    >
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("idleReport.percentageFuelSpentIdle", {
            _: "Percentage of total fuel spent during idle time",
          })}
        </div>
      </div>
      <CardContent
        style={{
          padding: 16,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !hasData ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", {
                _: "No data",
              })}
            </Typography>
          </Box>
        ) : chartOption ? (
          <ReactECharts
            option={chartOption}
            style={{ height: "100%", width: "100%", minHeight: 300 }}
          />
        ) : null}
      </CardContent>
    </Card>
  );
};

export default TotalFuelSpentIdleWidget;
